import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  departmentsService,
  Department,
  DepartmentMember,
  CreateDepartmentInput,
  UpdateDepartmentInput,
} from '@/services/api/departments';
import { usersService } from '@/services/api/users';
import { documentsService } from '@/services/api/documents';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Building2,
  Search,
  Pencil,
  Trash2,
  Users,
  Plus,
  UserCircle,
  Crown,
  FileText,
  X,
  Save,
  CheckCircle,
  MoreHorizontal,
  Calendar,
  UserPlus,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface FormData extends CreateDepartmentInput {
  isActive?: boolean;
  headUserId?: string;
}

const initialFormData: FormData = {
  name: '',
  description: '',
  code: '',
  isActive: true,
};

export function OrganizationPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<DepartmentMember | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  // For adding members
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'member' | 'manager' | 'head'>('member');

  // Fetch departments
  const { data, isLoading, error } = useQuery({
    queryKey: ['departments', searchQuery],
    queryFn: () => departmentsService.listDepartments({ search: searchQuery, includeInactive: true }),
  });

  // Fetch department members when a department is selected
  const { data: membersData, isLoading: loadingMembers } = useQuery({
    queryKey: ['department-members', selectedDepartment?.id],
    queryFn: () => departmentsService.getDepartmentMembers(selectedDepartment!.id),
    enabled: !!selectedDepartment?.id,
  });

  // Fetch all users for adding members
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.listUsers({ limit: 100 }),
    enabled: addMemberDialogOpen,
  });

  // Fetch documents for department (placeholder - actual implementation depends on API)
  const { data: documentsData } = useQuery({
    queryKey: ['department-documents', selectedDepartment?.id],
    queryFn: () => documentsService.listDocuments({ limit: 20 }),
    enabled: !!selectedDepartment?.id,
  });

  // Filter available users (not already in department)
  const availableUsers = usersData?.users.filter(
    (user) => !membersData?.members.find((m) => m.id === user.id)
  ) ?? [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (input: CreateDepartmentInput) => departmentsService.createDepartment(input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department created successfully');
      setIsCreating(false);
      setFormData(initialFormData);
      if (response) {
        setSelectedDepartment(response);
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to create department';
      toast.error('Failed to create department', { description: message });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDepartmentInput }) =>
      departmentsService.updateDepartment(id, input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department updated successfully');
      setIsEditing(false);
      if (response) {
        setSelectedDepartment(response);
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to update department';
      toast.error('Failed to update department', { description: message });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => departmentsService.deleteDepartment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedDepartment(null);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to delete department';
      toast.error('Failed to delete department', { description: message });
    },
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: () =>
      departmentsService.assignUser(selectedDepartment!.id, {
        userId: selectedUserId,
        departmentRole: selectedRole,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-members', selectedDepartment?.id] });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Member added successfully');
      setAddMemberDialogOpen(false);
      setSelectedUserId('');
      setSelectedRole('member');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to add member';
      toast.error('Failed to add member', { description: message });
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => departmentsService.removeUser(selectedDepartment!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-members', selectedDepartment?.id] });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Member removed successfully');
      setRemoveMemberDialogOpen(false);
      setSelectedMember(null);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to remove member';
      toast.error('Failed to remove member', { description: message });
    },
  });

  // Set head mutation
  const setHeadMutation = useMutation({
    mutationFn: (headUserId: string) =>
      departmentsService.updateDepartment(selectedDepartment!.id, { headUserId } as UpdateDepartmentInput),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['department-members', selectedDepartment?.id] });
      toast.success('Department head updated');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to update department head';
      toast.error('Failed to update head', { description: message });
    },
  });

  // Update form when selecting a department for editing
  useEffect(() => {
    if (selectedDepartment && isEditing) {
      setFormData({
        name: selectedDepartment.name,
        description: selectedDepartment.description || '',
        code: selectedDepartment.code || '',
        isActive: selectedDepartment.isActive,
        headUserId: selectedDepartment.headUserId || undefined,
      });
    }
  }, [selectedDepartment, isEditing]);

  const handleSelectDepartment = (dept: Department) => {
    setSelectedDepartment(dept);
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleCreateNew = () => {
    setSelectedDepartment(null);
    setIsCreating(true);
    setIsEditing(false);
    setFormData(initialFormData);
  };

  const handleEdit = () => {
    if (selectedDepartment) {
      setFormData({
        name: selectedDepartment.name,
        description: selectedDepartment.description || '',
        code: selectedDepartment.code || '',
        isActive: selectedDepartment.isActive,
        headUserId: selectedDepartment.headUserId || undefined,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (isCreating) {
      createMutation.mutate({
        name: formData.name,
        description: formData.description || undefined,
        code: formData.code || undefined,
      });
    } else if (isEditing && selectedDepartment) {
      updateMutation.mutate({
        id: selectedDepartment.id,
        input: {
          name: formData.name,
          description: formData.description || undefined,
          code: formData.code || undefined,
          isActive: formData.isActive,
          headUserId: formData.headUserId,
        },
      });
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedDepartment) {
      deleteMutation.mutate(selectedDepartment.id);
    }
  };

  const handleRemoveMember = (member: DepartmentMember) => {
    setSelectedMember(member);
    setRemoveMemberDialogOpen(true);
  };

  const confirmRemoveMember = () => {
    if (selectedMember) {
      removeMemberMutation.mutate(selectedMember.id);
    }
  };

  const getRoleBadge = (role: string, isHead: boolean) => {
    if (isHead) {
      return (
        <Badge className="bg-amber-500">
          <Crown className="mr-1 h-3 w-3" />
          Head
        </Badge>
      );
    }
    switch (role) {
      case 'manager':
        return <Badge variant="secondary">Manager</Badge>;
      default:
        return <Badge variant="outline">Member</Badge>;
    }
  };

  const departments = data?.departments || [];

  return (
    <div className="h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Organization</h1>
          <p className="text-muted-foreground">
            Manage departments and organizational structure
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Add Department
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-4">
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-bold">{data?.total || 0}</p>
            </div>
            <Building2 className="h-8 w-8 text-muted-foreground/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-xl font-bold text-green-600">
                {departments.filter((d) => d.isActive).length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Members</p>
              <p className="text-xl font-bold">
                {departments.reduce((acc, d) => acc + d.memberCount, 0)}
              </p>
            </div>
            <Users className="h-8 w-8 text-muted-foreground/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Departments Head</p>
              <p className="text-xl font-bold text-purple-600">
                {departments.filter((d) => d.headUserId).length}
              </p>
            </div>
            <Crown className="h-8 w-8 text-purple-500/30" />
          </div>
        </Card>
      </div>

      {/* Main Content - Master-Detail Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 h-[calc(100%-10rem)]">
        {/* Left Panel - Department List (40%) */}
        <Card className="lg:col-span-4 flex flex-col overflow-hidden">
          <CardHeader className="pb-3 flex-shrink-0">
            <CardTitle className="text-base">Departments</CardTitle>
            <div className="pt-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search departments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              {isLoading ? (
                <div className="space-y-2 p-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : error ? (
                <div className="py-8 text-center text-muted-foreground">
                  Failed to load departments.
                </div>
              ) : departments.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Building2 className="mx-auto h-12 w-12 text-muted-foreground/30 mb-4" />
                  <p>No departments found.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {departments.map((dept) => (
                    <div
                      key={dept.id}
                      onClick={() => handleSelectDepartment(dept)}
                      className={cn(
                        'p-3 cursor-pointer hover:bg-muted/50 transition-colors',
                        selectedDepartment?.id === dept.id && 'bg-muted'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <Building2 className="h-9 w-9 text-muted-foreground flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{dept.name}</p>
                            {dept.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {dept.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <Badge variant={dept.isActive ? 'default' : 'secondary'}>
                            {dept.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          {dept.code && <Badge variant="outline">{dept.code}</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {dept.memberCount} member{dept.memberCount !== 1 ? 's' : ''}
                        </span>
                        {dept.headUserName && (
                          <span className="flex items-center gap-1 truncate">
                            <Crown className="h-3 w-3" />
                            {dept.headUserName}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(dept.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Panel - Details/Edit (60%) */}
        <Card className="lg:col-span-6 flex flex-col overflow-hidden">
          {!selectedDepartment && !isCreating ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a Department</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Choose a department from the list to view details or create a new one.
                </p>
                <Button onClick={handleCreateNew}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Department
                </Button>
              </div>
            </div>
          ) : isCreating ? (
            // Create New Department Form
            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Create Department</h2>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      Department Information
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="name">Department Name *</Label>
                      <Input
                        id="name"
                        placeholder="e.g., Engineering"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="code">Code</Label>
                      <Input
                        id="code"
                        placeholder="e.g., ENG"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        A short code for the department (optional)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Department description..."
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!formData.name || createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Department'}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : selectedDepartment && isEditing ? (
            // Edit Department Form
            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Edit Department</h2>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      Department Information
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-name">Department Name *</Label>
                      <Input
                        id="edit-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-code">Code</Label>
                      <Input
                        id="edit-code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-description">Description</Label>
                      <Textarea
                        id="edit-description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Active Status</Label>
                        <p className="text-xs text-muted-foreground">
                          Inactive departments are hidden from users
                        </p>
                      </div>
                      <Switch
                        checked={formData.isActive}
                        onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!formData.name || updateMutation.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : selectedDepartment ? (
            // View Department Details
            <Tabs defaultValue="overview" className="flex-1 flex flex-col">
              <div className="p-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-semibold">{selectedDepartment.name}</h2>
                        {selectedDepartment.code && (
                          <Badge variant="outline">{selectedDepartment.code}</Badge>
                        )}
                      </div>
                      {selectedDepartment.description && (
                        <p className="text-sm text-muted-foreground">{selectedDepartment.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={handleDelete}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant={selectedDepartment.isActive ? 'default' : 'secondary'}>
                    {selectedDepartment.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {selectedDepartment.memberCount} member{selectedDepartment.memberCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <TabsList className="mx-4 mt-2 w-fit">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="members">Members</TabsTrigger>
                <TabsTrigger value="files">Files</TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1">
                <TabsContent value="overview" className="p-4 m-0">
                  {/* Quick Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Members</span>
                      </div>
                      <p className="text-xl font-bold mt-1">{selectedDepartment.memberCount}</p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Head</span>
                      </div>
                      <p className="text-sm font-medium mt-1 truncate">
                        {selectedDepartment.headUserName || 'Not assigned'}
                      </p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Status</span>
                      </div>
                      <div className="mt-1">
                        <Badge variant={selectedDepartment.isActive ? 'default' : 'secondary'}>
                          {selectedDepartment.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Created</span>
                      </div>
                      <p className="text-sm font-medium mt-1">
                        {formatDistanceToNow(new Date(selectedDepartment.createdAt), { addSuffix: true })}
                      </p>
                    </Card>
                  </div>

                  {/* Details */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Department Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Name</p>
                          <p className="font-medium mt-1">{selectedDepartment.name}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Code</p>
                          <p className="font-medium mt-1">{selectedDepartment.code || '-'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-muted-foreground">Description</p>
                          <p className="font-medium mt-1">{selectedDepartment.description || '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Department ID</p>
                          <p className="font-mono text-xs mt-1">{selectedDepartment.id}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Created At</p>
                          <p className="font-medium mt-1">
                            {new Date(selectedDepartment.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="members" className="p-4 m-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Department Members
                          </CardTitle>
                          <CardDescription>
                            People in this department
                          </CardDescription>
                        </div>
                        <Button size="sm" onClick={() => setAddMemberDialogOpen(true)}>
                          <UserPlus className="mr-2 h-4 w-4" />
                          Add Member
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loadingMembers ? (
                        <div className="space-y-2">
                          {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                          ))}
                        </div>
                      ) : membersData?.members && membersData.members.length > 0 ? (
                        <div className="space-y-2">
                          {membersData.members.map((member) => {
                            const isHead = selectedDepartment.headUserId === member.id;
                            return (
                              <div
                                key={member.id}
                                className="flex items-center justify-between p-3 border rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <UserCircle className="h-9 w-9 text-muted-foreground" />
                                  <div>
                                    <p className="font-medium">
                                      {member.firstName} {member.lastName}
                                    </p>
                                    <p className="text-xs text-muted-foreground">{member.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {getRoleBadge(member.departmentRole, isHead)}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {!isHead && (
                                        <DropdownMenuItem onClick={() => setHeadMutation.mutate(member.id)}>
                                          <Crown className="mr-2 h-4 w-4" />
                                          Set as Head
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem
                                        onClick={() => handleRemoveMember(member)}
                                        className="text-destructive"
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Remove
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
                          <p>No members in this department</p>
                          <p className="text-sm">Add members to get started.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="files" className="p-4 m-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Department Files
                      </CardTitle>
                      <CardDescription>
                        Documents this department has access to
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {documentsData?.documents && documentsData.documents.length > 0 ? (
                        <div className="space-y-2">
                          {documentsData.documents.slice(0, 10).map((doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-8 w-8 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{doc.title || doc.originalFilename}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true })}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline">{doc.status}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
                          <p>No files shared with this department</p>
                          <p className="text-sm">Upload documents in Knowledge Base and share with this department.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          ) : null}
        </Card>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Department</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedDepartment?.name}</strong>?
              This action cannot be undone. All member assignments will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Member Confirmation */}
      <AlertDialog open={removeMemberDialogOpen} onOpenChange={setRemoveMemberDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <strong>
                {selectedMember?.firstName} {selectedMember?.lastName}
              </strong>{' '}
              from this department?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeMemberMutation.isPending ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Member Dialog */}
      <AlertDialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add Member</AlertDialogTitle>
            <AlertDialogDescription>
              Add a user to {selectedDepartment?.name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      No available users
                    </div>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Role in Department</Label>
              <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="head">Head</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => addMemberMutation.mutate()}
              disabled={!selectedUserId || addMemberMutation.isPending}
            >
              {addMemberMutation.isPending ? 'Adding...' : 'Add Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
